import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  FileText,
  RefreshCcw,
  CalendarClock,
  Archive,
  BarChart3,
  Users,
  Palette,
  Bell,
  HelpCircle,
  Plus,
  Sparkles,
} from "lucide-react";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const navItems = [
  { label: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { label: "Templates", url: "/templates", icon: FileText },
  { label: "Cycles", url: "/cycles", icon: RefreshCcw },
  { label: "Schedules", url: "/schedules", icon: CalendarClock },
  { label: "Archive", url: "/archive", icon: Archive },
  { label: "Analytics", url: "/analytics", icon: BarChart3 },
  { label: "Contributors", url: "/admin/contributors", icon: Users },
  { label: "Brand Kit", url: "/settings/brand", icon: Palette },
  { label: "Notifications", url: "/settings/notifications", icon: Bell },
  { label: "How It Works", url: "/how-it-works", icon: HelpCircle },
];

const actions = [
  { label: "Create New Cycle", url: "/cycles", icon: Plus },
  { label: "Create Schedule", url: "/schedules", icon: Plus },
  { label: "Create Template", url: "/templates", icon: Plus },
];

export const CommandPalette = ({ open, onOpenChange }: CommandPaletteProps) => {
  const navigate = useNavigate();

  const { data: cycles } = useQuery({
    queryKey: ["palette_cycles"],
    queryFn: async () => {
      const { data } = await supabase
        .from("report_cycles")
        .select("id, title, status")
        .order("created_at", { ascending: false })
        .limit(15);
      return data || [];
    },
    enabled: open,
  });

  const { data: templates } = useQuery({
    queryKey: ["palette_templates"],
    queryFn: async () => {
      const { data } = await supabase
        .from("report_templates")
        .select("id, name")
        .limit(15);
      return data || [];
    },
    enabled: open,
  });

  const go = (url: string) => {
    navigate(url);
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search cycles, templates, pages..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Quick Actions">
          {actions.map((a) => (
            <CommandItem key={a.label} onSelect={() => go(a.url)}>
              <a.icon className="w-4 h-4 mr-2" />
              {a.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Navigate">
          {navItems.map((item) => (
            <CommandItem key={item.url} onSelect={() => go(item.url)}>
              <item.icon className="w-4 h-4 mr-2" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {cycles && cycles.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Cycles">
              {cycles.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`cycle ${c.title}`}
                  onSelect={() => go(`/cycles/${c.id}`)}
                >
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  <span className="truncate">{c.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {templates && templates.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Templates">
              {templates.map((t) => (
                <CommandItem
                  key={t.id}
                  value={`template ${t.name}`}
                  onSelect={() => go(`/templates/${t.id}`)}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  <span className="truncate">{t.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};
